import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  verifie: { bg: 'bg-emerald-600/20 border-emerald-500/40 text-emerald-300', icon: '✅', label: 'Compte vérifié', msg: 'Votre compte est vérifié. Vous avez accès à toutes les fonctionnalités.' },
  en_attente: { bg: 'bg-blue-600/20 border-blue-500/40 text-blue-300', icon: '⏳', label: 'En attente de vérification', msg: 'Téléversez vos documents puis soumettez votre dossier. Réponse sous 48h ouvrées.' },
  refuse: { bg: 'bg-red-600/20 border-red-500/40 text-red-300', icon: '❌', label: 'Dossier refusé', msg: 'Votre dossier a été refusé. Remplacez les documents concernés et soumettez à nouveau.' },
  non_applicable: { bg: 'bg-blue-600/20 border-blue-500/40 text-blue-300', icon: '⏳', label: 'Vérification requise', msg: 'Téléversez vos documents pour faire vérifier votre compte.' }
};

const DocumentSlot = ({ slot, onUpload, uploading }) => {
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      alert('Fichier trop volumineux (maximum 8 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      onUpload(slot.doc_type, file.name, file.type || 'application/octet-stream', base64);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div data-testid={`doc-slot-${slot.doc_type}`} className={`border rounded-xl p-6 transition-all duration-300 ${slot.fourni ? 'bg-emerald-600/10 border-emerald-500/30' : 'bg-slate-700/30 border-slate-600/40'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className={`rounded-full p-3 text-2xl ${slot.fourni ? 'bg-emerald-600/20' : 'bg-slate-600/30'}`}>
            {slot.fourni ? '📄' : '📎'}
          </div>
          <div>
            <h4 className="text-white font-semibold">{slot.label}</h4>
            {slot.fourni ? (
              <p className="text-emerald-400 text-sm mt-1">
                ✓ {slot.filename}
                {slot.uploaded_at && <span className="text-slate-400"> — {new Date(slot.uploaded_at).toLocaleDateString('fr-FR')}</span>}
              </p>
            ) : (
              <p className="text-slate-400 text-sm mt-1">PDF, JPG ou PNG — 8 Mo max</p>
            )}
          </div>
        </div>
        <button
          data-testid={`upload-btn-${slot.doc_type}`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 disabled:opacity-50 ${
            slot.fourni
              ? 'bg-slate-600/40 hover:bg-slate-600/60 text-slate-200'
              : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
          }`}
        >
          {uploading ? 'Envoi...' : slot.fourni ? 'Remplacer' : 'Téléverser'}
        </button>
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
};

export const VerificationDocuments = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/verification/documents`);
      setData(response.data);
    } catch (error) {
      console.error('Erreur chargement vérification:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleUpload = async (docType, filename, contentType, base64) => {
    setUploadingType(docType);
    setMessage('');
    try {
      await axios.post(`${API}/verification/documents`, {
        doc_type: docType,
        filename,
        content_type: contentType,
        data_base64: base64
      });
      await fetchStatus();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploadingType(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setMessage('');
    try {
      const response = await axios.post(`${API}/verification/soumettre`);
      setMessage(response.data.message);
      await fetchStatus();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  if (!data) return null;

  const statusCfg = STATUS_CONFIG[data.verification_status] || STATUS_CONFIG.en_attente;
  const isVerified = data.verification_status === 'verifie';

  return (
    <div className="space-y-6" data-testid="verification-documents">
      <div>
        <h3 className="text-2xl font-bold text-white mb-2 flex items-center">
          <span className="mr-3">🛡️</span>
          Vérification de mon compte
        </h3>
        <p className="text-slate-400">
          Téléversez vos documents dans les emplacements prévus pour faire vérifier votre compte professionnel.
        </p>
      </div>

      <div data-testid="verification-status-banner" className={`border rounded-xl p-5 flex items-start space-x-4 ${statusCfg.bg}`}>
        <span className="text-3xl">{statusCfg.icon}</span>
        <div>
          <p className="font-semibold">{statusCfg.label}</p>
          <p className="text-sm opacity-90 mt-1">{statusCfg.msg}</p>
          {data.verification_status === 'refuse' && data.motif_refus && (
            <p className="text-sm mt-2 font-medium">Motif : {data.motif_refus}</p>
          )}
        </div>
      </div>

      {!isVerified && (
        <>
          <div className="space-y-4">
            {data.documents.map((slot) => (
              <DocumentSlot key={slot.doc_type} slot={slot} onUpload={handleUpload} uploading={uploadingType === slot.doc_type} />
            ))}
          </div>

          {message && (
            <div className="p-4 bg-green-500/20 border border-green-500/30 text-green-200 rounded-lg" data-testid="verification-success-message">
              {message}
            </div>
          )}

          {data.deja_soumis ? (
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-lg text-center">
              📨 Dossier soumis — en cours d'examen par notre équipe
            </div>
          ) : (
            <button
              data-testid="submit-dossier-btn"
              onClick={handleSubmit}
              disabled={!data.tous_fournis || submitting}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium py-4 px-6 rounded-xl transition-all duration-300 shadow-lg"
            >
              {submitting ? 'Envoi en cours...' : data.tous_fournis ? '📨 Soumettre mon dossier pour vérification' : `Téléversez tous les documents pour soumettre (${data.documents.filter(d => d.fourni).length}/${data.documents.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default VerificationDocuments;
